import * as React from 'react'
import { CheckCircle, File, WarningCircle, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { ProgressBar } from './progress-bar'

/**
 * FileCard — one row in a file list: a file-type glyph, the name, a meta line
 * (size / count), an optional upload progress bar, and a trailing slot for a
 * status indicator and/or a remove button.
 *
 * Pairs with <FileDropzone> — the dropzone is the input, FileCard renders each
 * picked/uploading/uploaded file. State is caller-owned: drive `status` +
 * `progress` from your upload logic.
 *
 *   status   uploading | complete | error   (drives the default trailing
 *            indicator + error accent; omit for a plain row)
 *   size     sm | md   (md is the default; sm is the compact row for tight
 *            surfaces like an email compose attachment list)
 *
 * When neither `trailing` nor `onRemove` is given, a `complete`/`error` status
 * renders a default check / warning icon. Provide `trailing` to override it
 * (e.g. a <Badge>), and/or `onRemove` to add a dismiss button.
 */

type FileCardStatus = 'uploading' | 'complete' | 'error'
type FileCardSize = 'sm' | 'md'

const sizeStyles: Record<
  FileCardSize,
  {
    card: string
    iconBox: string
    name: string
    meta: string
    statusIcon: string
    progressGap: string
  }
> = {
  sm: {
    card: 'rounded-lg p-2.5',
    iconBox: 'size-8 [&>svg]:size-5',
    name: 'text-xs',
    meta: 'text-xs',
    statusIcon: 'size-4',
    progressGap: 'mt-2',
  },
  md: {
    card: 'rounded-xl p-3.5',
    iconBox: 'size-10 [&>svg]:size-7',
    name: 'text-sm',
    meta: 'text-xs',
    statusIcon: 'size-5',
    progressGap: 'mt-2.5',
  },
}

export interface FileCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** File name. */
  name: React.ReactNode
  /** Secondary line — size, "250 KB of 1 MB", "15 sec left", etc. */
  meta?: React.ReactNode
  /** File-type glyph (defaults to a generic file icon). */
  icon?: React.ReactNode
  status?: FileCardStatus
  size?: FileCardSize
  /** Upload progress 0–100; renders a progress bar when provided. */
  progress?: number
  /** Renders a trailing ghost dismiss button that calls this. */
  onRemove?: () => void
  /**
   * Accessible name for the remove button. Give each card a distinct label
   * (e.g. `Remove ${name}`) when several render side by side. Default "Remove file".
   */
  removeLabel?: string
  /** Custom trailing content (status badge/icon). Overrides the status default. */
  trailing?: React.ReactNode
}

/** Default trailing indicator for a status, when the caller gives none. */
function StatusIndicator({
  status,
  className,
}: {
  status: FileCardStatus
  className?: string
}) {
  if (status === 'complete')
    return (
      <CheckCircle
        weight="fill"
        aria-label="Upload complete"
        className={cn('text-fbui-green-600', className)}
      />
    )
  if (status === 'error')
    return (
      <WarningCircle
        weight="fill"
        aria-label="Upload failed"
        className={cn('text-fbui-red-600', className)}
      />
    )
  return null
}

export const FileCard = React.forwardRef<HTMLDivElement, FileCardProps>(
  (
    {
      className,
      name,
      meta,
      icon,
      status,
      size = 'md',
      progress,
      onRemove,
      removeLabel = 'Remove file',
      trailing,
      ...props
    },
    ref
  ) => {
    const s = sizeStyles[size]
    const showProgress =
      progress != null && status !== 'complete' && status !== 'error'
    const trailingContent =
      trailing ??
      (status ? <StatusIndicator status={status} className={s.statusIcon} /> : null)

    return (
      <div
        ref={ref}
        data-slot="file-card"
        data-status={status}
        className={cn(
          'border bg-fbui-base-white',
          s.card,
          status === 'error' ? 'border-fbui-red-200' : 'border-fbui-gray-200',
          className
        )}
        {...props}
      >
        <div className="flex items-start gap-2.5">
          <span
            aria-hidden="true"
            className={cn(
              'flex shrink-0 items-center justify-center text-fbui-gray-400',
              s.iconBox
            )}
          >
            {icon ?? <File weight="fill" />}
          </span>
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'truncate font-semibold text-fbui-gray-900',
                s.name
              )}
            >
              {name}
            </p>
            {meta != null && (
              <p className={cn('mt-0.5 text-fbui-gray-500', s.meta)}>{meta}</p>
            )}
          </div>
          {(trailingContent || onRemove) && (
            <div className="flex shrink-0 items-center gap-1">
              {trailingContent}
              {onRemove && (
                <Button
                  variant="neutral"
                  size="xs"
                  iconOnly
                  aria-label={removeLabel}
                  onClick={onRemove}
                  // Removal is destructive — keep the neutral base but override
                  // just the hover to red so it doesn't read as a positive action.
                  className="hover:bg-fbui-red-50 hover:text-fbui-red-600"
                >
                  <X />
                </Button>
              )}
            </div>
          )}
        </div>
        {showProgress && (
          <ProgressBar value={progress} label="right" className={s.progressGap} />
        )}
      </div>
    )
  }
)
FileCard.displayName = 'FileCard'
